import { Controller } from '@hotwired/stimulus';

const LINE_START_CLASS = 'upload-controls__group--line-start';

// Keeps the wrapping toolbar tidy at any width. When the groups overflow one line, the
// break target pushes the whole Location/Vote ID/Load set onto line two together; each
// group opening a line is then flagged so its leading divider can be hidden.
export default class extends Controller<HTMLElement> {
  static targets = ['break'];

  declare readonly breakTarget: HTMLElement;
  declare readonly hasBreakTarget: boolean;

  private observer?: ResizeObserver;

  connect() {
    this.observer = new ResizeObserver(() => this.layout());
    this.observer.observe(this.element);
    // Groups are observed too: hiding one (CSV mode) reflows the row without resizing it.
    this.groups().forEach((group) => this.observer!.observe(group));
  }

  disconnect() {
    this.observer?.disconnect();
  }

  private layout() {
    if (this.hasBreakTarget) {
      // Measured with the break hidden: while it shows, the record group stretches to fill
      // its line and would always read as overflowing.
      this.breakTarget.classList.add('hidden');
      this.breakTarget.classList.toggle('hidden', !this.overflows());
    }
    this.markLineStarts();
  }

  // Summed from natural widths rather than read off the current wrap.
  private overflows(): boolean {
    const visible = this.groups().filter((group) => group.getBoundingClientRect().width > 0);
    const gap = parseFloat(getComputedStyle(this.element).columnGap) || 0;
    const total = visible.reduce((sum, group) => sum + group.getBoundingClientRect().width, 0)
      + gap * Math.max(visible.length - 1, 0);

    return total > this.element.clientWidth;
  }

  private markLineStarts() {
    let lineBottom = -Infinity;

    this.groups().forEach((group) => {
      const rect = group.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        group.classList.remove(LINE_START_CLASS);
        return;
      }

      // Groups share a line but not a top (align-items: center), so compare against the
      // tallest bottom seen on the current line.
      const startsLine = rect.top >= lineBottom - 1;
      group.classList.toggle(LINE_START_CLASS, startsLine);
      lineBottom = startsLine ? rect.bottom : Math.max(lineBottom, rect.bottom);
    });
  }

  private groups(): HTMLElement[] {
    return Array.from(this.element.children)
      .filter((child) => !(this.hasBreakTarget && child === this.breakTarget)) as HTMLElement[];
  }
}
